import { useEffect, useState } from "react";
import { Volume2, BookOpen, Sparkles } from "lucide-react";

/**
 * One-time voice-preference modal shown on the family's first /chat visit.
 * Doubles as the required user-gesture that unlocks Audio.play() in Chrome/
 * Safari — after the user clicks either button, `onChoose(voiceEnabled)` fires
 * and the parent can safely start playing Asher's audio.
 *
 * The choice is persisted to localStorage under `asher_voice_choice_made`
 * so the modal never appears again on the same device. Families can still
 * change their mind any time via the "Prefer to read?" chip or the header
 * speaker icon.
 */
export default function AsherVoiceOnboardingModal({ onChoose }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      const already = window.localStorage.getItem("asher_voice_choice_made");
      if (!already) setOpen(true);
    } catch (_) {
      // No localStorage (private mode?) — surface the modal anyway.
      setOpen(true);
    }
  }, []);

  const decide = (voiceEnabled) => {
    try {
      window.localStorage.setItem("asher_voice_choice_made", "1");
      window.localStorage.setItem("asher_voice_enabled", String(voiceEnabled));
    } catch (_) { /* no-op */ }
    setOpen(false);
    if (typeof onChoose === "function") onChoose(voiceEnabled);
  };

  if (!open) return null;

  return (
    <div
      data-testid="voice-onboarding-modal"
      className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="voice-onboarding-title"
    >
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden">
        <div className="bg-gradient-to-br from-primary to-primary-dark px-6 py-6 text-white">
          <div className="flex items-center gap-2 opacity-90 mb-2">
            <Sparkles className="w-4 h-4" />
            <span className="text-xs font-body font-semibold uppercase tracking-wider">A quick choice</span>
          </div>
          <h2 id="voice-onboarding-title" className="text-2xl md:text-3xl font-heading font-bold leading-tight">
            How would you like to meet Asher?
          </h2>
          <p className="text-sm mt-2 font-body opacity-90">
            He can speak to you aloud, or stay quiet so you can read at your own pace. You can change this any time.
          </p>
        </div>

        <div className="p-6 grid gap-3">
          <button
            type="button"
            data-testid="voice-onboarding-speak-btn"
            onClick={() => decide(true)}
            className="w-full text-left group rounded-2xl border-2 border-primary/20 hover:border-primary bg-white hover:bg-primary/5 transition-all p-5 flex items-start gap-4"
          >
            <div className="flex-shrink-0 w-11 h-11 rounded-full bg-primary/10 group-hover:bg-primary/20 flex items-center justify-center transition-colors">
              <Volume2 className="w-5 h-5 text-primary" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-heading font-bold text-text-primary">Speak to me aloud</div>
              <div className="text-sm font-body text-text-secondary mt-0.5">
                A warm American voice — great when your hands are full or eyes are tired.
              </div>
            </div>
          </button>

          <button
            type="button"
            data-testid="voice-onboarding-read-btn"
            onClick={() => decide(false)}
            className="w-full text-left group rounded-2xl border-2 border-[#E2E8F0] hover:border-primary/40 bg-white hover:bg-slate-50 transition-all p-5 flex items-start gap-4"
          >
            <div className="flex-shrink-0 w-11 h-11 rounded-full bg-slate-100 group-hover:bg-slate-200 flex items-center justify-center transition-colors">
              <BookOpen className="w-5 h-5 text-slate-600" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-heading font-bold text-text-primary">I&apos;ll read on my own</div>
              <div className="text-sm font-body text-text-secondary mt-0.5">
                Text only — perfect when you need quiet, or if you&apos;re in a shared space.
              </div>
            </div>
          </button>
        </div>

        <div className="px-6 pb-5 pt-1">
          <p className="text-[11px] font-body text-text-secondary text-center">
            Your choice is saved on this device only — never linked to your account.
          </p>
        </div>
      </div>
    </div>
  );
}
